/**
 * Unified column schema registry for ranks table modes.
 * This centralizes column IDs, labels, sort behavior, and semantic classes.
 */

/**
 * @typedef {"template"|"component"|"creator"} TableType
 */

const STANDARD_COLUMNS = [
  {
    id: "rank",
    label: "Rank",
    modeVisibility: ["template", "component"],
    sortable: true,
    defaultSortDirection: "asc",
    headerClass: "col-rank",
    cellClass: "col-rank",
    footerClass: "col-rank",
  },
  {
    id: "change",
    labelHtml:
      '<span class="with-tooltip change-header-tooltip"><span class="sortable-header">1-day Δ</span><div class="tooltip right"><div class="tooltip-item">1-day delta AKA 1-day change AKA how the rank moved since yesterday</div></div></span>',
    modeVisibility: ["template", "component"],
    sortable: true,
    defaultSortDirection: "desc",
    headerClass: "col-change",
    cellClass: "col-change",
    footerClass: "col-change",
  },
  {
    id: "trend",
    label: "7-day Trend",
    modeVisibility: ["template", "component"],
    sortable: false,
    defaultSortDirection: "asc",
    headerClass: "col-trend",
    cellClass: "col-trend",
    footerClass: "col-trend",
  },
  {
    id: "price",
    label: "Price",
    modeVisibility: ["template", "component"],
    sortable: true,
    defaultSortDirection: "asc",
    headerClass: "col-price",
    cellClass: "col-price",
    footerClass: "col-price",
  },
  {
    id: "image",
    label: "Img",
    modeVisibility: ["template", "component"],
    sortable: false,
    defaultSortDirection: "asc",
    headerClass: "col-img image-column",
    cellClass: "col-img image-column",
    footerClass: "col-img image-column",
  },
  {
    id: "name",
    label: "Name",
    modeVisibility: ["template", "component"],
    sortable: true,
    defaultSortDirection: "asc",
    headerClass: "col-name",
    cellClass: "col-name",
    footerClass: "col-name",
  },
  {
    id: "medals",
    labelHtml:
      '<span class="sortable-header">Medals</span><span class="header-new-pill" aria-label="New feature">New!</span>',
    modeVisibility: ["template", "component"],
    sortable: true,
    defaultSortDirection: "desc",
    headerClass: "col-medals",
    cellClass: "col-medals",
    footerClass: "col-medals",
  },
  {
    id: "author",
    label: "Creator",
    modeVisibility: ["template", "component"],
    sortable: true,
    defaultSortDirection: "asc",
    headerClass: "col-author",
    cellClass: "col-author",
    footerClass: "col-author",
  },
];

const CREATOR_COLUMNS = [
  {
    id: "name",
    label: "Name",
    modeVisibility: ["creator"],
    sortable: true,
    defaultSortDirection: "asc",
    headerClass: "creators-col-name col-creator-name",
    cellClass: "creator-cell-name creators-col-name col-creator-name",
    footerClass: "col-creator-name",
  },
  {
    id: "templates",
    label: "Templates",
    modeVisibility: ["creator"],
    sortable: true,
    defaultSortDirection: "desc",
    headerClass: "creators-col-metric col-creator-templates",
    cellClass: "creator-cell-number creators-col-metric col-creator-templates",
    footerClass: "col-creator-templates",
  },
  {
    id: "templatesdelta",
    labelHtml: '<span class="sortable-header">1-day Δ</span>',
    modeVisibility: ["creator"],
    sortable: true,
    defaultSortDirection: "desc",
    headerClass: "creators-col-delta col-creator-templates-delta",
    cellClass: "creator-cell-number creators-col-delta col-creator-templates-delta",
    footerClass: "col-creator-templates-delta",
  },
  {
    id: "components",
    label: "Components",
    modeVisibility: ["creator"],
    sortable: true,
    defaultSortDirection: "desc",
    headerClass: "creators-col-metric col-creator-components",
    cellClass: "creator-cell-number creators-col-metric col-creator-components",
    footerClass: "col-creator-components",
  },
  {
    id: "componentsdelta",
    labelHtml: '<span class="sortable-header">1-day Δ</span>',
    modeVisibility: ["creator"],
    sortable: true,
    defaultSortDirection: "desc",
    headerClass: "creators-col-delta col-creator-components-delta",
    cellClass: "creator-cell-number creators-col-delta col-creator-components-delta",
    footerClass: "col-creator-components-delta",
  },
  {
    id: "total",
    label: "Total",
    modeVisibility: ["creator"],
    sortable: true,
    defaultSortDirection: "desc",
    headerClass: "creators-col-metric col-creator-total",
    cellClass: "creator-cell-number creators-col-metric col-creator-total",
    footerClass: "col-creator-total",
  },
  {
    id: "totaldelta",
    labelHtml: '<span class="sortable-header">1-day Δ</span>',
    modeVisibility: ["creator"],
    sortable: true,
    defaultSortDirection: "desc",
    headerClass: "creators-col-delta col-creator-total-delta",
    cellClass: "creator-cell-number creators-col-delta col-creator-total-delta",
    footerClass: "col-creator-total-delta",
  },
];

function getColumnsInternal(type) {
  if (type === "creator") return CREATOR_COLUMNS;
  return STANDARD_COLUMNS;
}

export function getColumnsForType(type) {
  return [...getColumnsInternal(type)];
}

export function getSortableColumnsForType(type) {
  return new Set(
    getColumnsInternal(type)
      .filter((column) => column.sortable)
      .map((column) => column.id),
  );
}

export function getDefaultSortForType(type) {
  const columns = getColumnsInternal(type);
  const preferred = type === "creator" ? "total" : "rank";
  const preferredColumn = columns.find((column) => column.id === preferred && column.sortable);
  if (preferredColumn) {
    return {
      sortColumn: preferredColumn.id,
      sortDirection: preferredColumn.defaultSortDirection || "asc",
    };
  }

  const firstSortable = columns.find((column) => column.sortable);
  if (!firstSortable) return { sortColumn: "rank", sortDirection: "asc" };
  return {
    sortColumn: firstSortable.id,
    sortDirection: firstSortable.defaultSortDirection || "asc",
  };
}

export function getDefaultDirection(type, columnId) {
  const column = getColumnsInternal(type).find((item) => item.id === columnId);
  return column?.defaultSortDirection || "asc";
}

function getMedalSortScore(item) {
  if (item?.type !== "template" && item?.type !== "component") return 0;
  const score = Number(item?.badges?.medalScore);
  return Number.isFinite(score) && score > 0 ? score : 0;
}

export function getColumnSortValue(item, column, context = "all") {
  if (item?.type === "creator") {
    const creatorStats = item.creatorStats || {};
    switch (column) {
      case "rank":
        return null;
      case "name":
        return String(item.name || "").toLowerCase();
      case "templates":
        return creatorStats.totalTemplates ?? 0;
      case "templatesdelta":
        return creatorStats.totalTemplatesDelta1d ?? null;
      case "components":
        return creatorStats.totalComponents ?? 0;
      case "componentsdelta":
        return creatorStats.totalComponentsDelta1d ?? null;
      case "total":
        return creatorStats.totalItems ?? 0;
      case "totaldelta":
        return creatorStats.totalItemsDelta1d ?? null;
      case "medals":
        return 0;
      case "author":
        return "";
      default:
        return null;
    }
  }

  switch (column) {
    case "rank": {
      const entry = item.ranks?.[context];
      return entry ? entry[0] : null;
    }
    case "change": {
      const entry = item.ranks?.[context];
      const change = entry ? entry[1] : null;
      if (change === "new") return 1000003;
      if (change === "moved-in") return 1000002;
      if (change === "comeback") return 1000001;
      if (change === "price_change") return 1000000;
      if (typeof change === "number") return change;
      return -Infinity;
    }
    case "price": {
      if (item.price === "Free") return -1;
      const priceNum = parseFloat(String(item.price).replace(/[^0-9.]/g, "")) || 0;
      return priceNum;
    }
    case "name":
      return String(item.name || "").toLowerCase();
    case "medals":
      return getMedalSortScore(item);
    case "author":
      return String(item.authorName || "").toLowerCase();
    default:
      return null;
  }
}
